import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { useAuth } from "../../../firebase/AuthContext";
import { getApiUrl } from "../../../config/api";
import Sidebar from "../../../components/Sidebar";
import LanguageSwitcher from "../../../components/LanguageSwitcher";
import TrendList from "./tabs/TrendList";
import CustomizationTab from "./tabs/CustomizationTab";
import NewsletterTab from "./tabs/newsletter/NewsletterTab";
import { t } from "./shared/trendReportShared";

// About a month of daily reports, and fills the 1/2/3-column grid evenly.
const REPORTS_PER_PAGE = 30;

const TAB_DEFAULT = {
  reports: {
    id: "reports",
    iconPath:
      "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
    labelEn: "All Reports",
    labelZh: "所有報告",
  },
};

export default function TrendReportsFeedPage() {
  const { currentUser, hasRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const getLangFromUrl = () => {
    const params = new URLSearchParams(location.search);
    return params.get("lang") === "zh" ? "zh" : "en";
  };
  const [language, setLanguage] = useState(getLangFromUrl());
  useEffect(() => {
    setLanguage(getLangFromUrl());
    // eslint-disable-next-line
  }, [location.search]);
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("lang") !== language) {
      params.set("lang", language);
      navigate({ search: params.toString() }, { replace: true });
    }
    // eslint-disable-next-line
  }, [language]);

  const canManagePriority = Boolean(hasRole && hasRole("trend.priority_items.manage"));
  const canSendNewsletter = Boolean(hasRole && hasRole("trend.newsletter.send"));
  const canManageSubscribers = Boolean(hasRole && hasRole("trend.subscribers.manage"));

  const TAB_CONFIG = useMemo(() => {
    return {
      ...TAB_DEFAULT,
      ...(canManagePriority && {
        customization: {
          id: "customization",
          iconPath:
            "M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12",
          labelEn: "Customization",
          labelZh: "客製化",
        },
      }),
      ...((canSendNewsletter || canManageSubscribers) && {
        newsletter: {
          id: "newsletter",
          iconPath:
            "M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z",
          labelEn: t("dailyEmailTab", "en"),
          labelZh: t("dailyEmailTab", "zh"),
        },
      }),
    };
  }, [canManagePriority, canSendNewsletter, canManageSubscribers]);

  const [activeTab, setActiveTab] = useState("reports");
  useEffect(() => {
    if (!(activeTab in TAB_CONFIG)) {
      setActiveTab("reports");
    }
  }, [TAB_CONFIG, activeTab]);

  // ── Data: report list (GET /trend-reports/list) ────────────────────────────
  // page / from / to live in the URL so coming back from a report (the detail
  // page's back button or the browser's) lands on the same page and range.
  const listParams = new URLSearchParams(location.search);
  const page = Math.max(1, parseInt(listParams.get("page"), 10) || 1);
  const dateFrom = listParams.get("from") || "";
  const dateTo = listParams.get("to") || "";

  const setListQuery = (next) => {
    const params = new URLSearchParams(location.search);
    Object.entries(next).forEach(([key, value]) =>
      value ? params.set(key, String(value)) : params.delete(key),
    );
    navigate({ search: params.toString() });
  };

  const [reportsState, setReportsState] = useState({
    status: "loading",
    reports: [],
    pagination: { page: 1, per_page: REPORTS_PER_PAGE, total: 0, pages: 1 },
    latestDate: null,
  });

  const fetchReports = useCallback(
    async (signal) => {
      if (!currentUser) return;
      // YYYY-MM-DD strings compare correctly as plain strings.
      if (dateFrom && dateTo && dateFrom > dateTo) {
        setReportsState((s) => ({ ...s, status: "badRange", reports: [] }));
        return;
      }
      setReportsState((s) => ({ ...s, status: "loading" }));
      try {
        const token = await currentUser.getIdToken();
        // The date inputs' raw YYYY-MM-DD goes straight through: report_date is
        // a Taipei calendar day, and new Date() would shift it by timezone.
        const params = new URLSearchParams({ page, per_page: REPORTS_PER_PAGE });
        if (dateFrom) params.set("from", dateFrom);
        if (dateTo) params.set("to", dateTo);
        const res = await axios.get(
          `${getApiUrl()}/api/v2/trend-reports/list?${params.toString()}`,
          { headers: { Authorization: `Bearer ${token}` }, signal },
        );
        setReportsState({
          status: "ok",
          reports: res.data?.reports || [],
          pagination: res.data?.pagination || {
            page: 1,
            per_page: REPORTS_PER_PAGE,
            total: 0,
            pages: 1,
          },
          latestDate: res.data?.latest_report_date || null,
        });
      } catch (e) {
        // Aborted by a newer page / range change - that request owns the state.
        if (axios.isCancel(e)) return;
        setReportsState((s) => ({ ...s, status: "error", reports: [] }));
      }
    },
    [currentUser, page, dateFrom, dateTo],
  );

  useEffect(() => {
    const controller = new AbortController();
    fetchReports(controller.signal);
    return () => controller.abort();
  }, [fetchReports]);

  // ── Data: priority-upload queue (GET /trend-reports/priority-items) ────────
  const [queueState, setQueueState] = useState({
    status: "loading",
    reportDate: null,
    items: [],
  });

  const fetchQueue = useCallback(async () => {
    if (!currentUser || !canManagePriority) return;
    setQueueState((s) => ({ ...s, status: "loading" }));
    try {
      const token = await currentUser.getIdToken();
      const res = await axios.get(
        `${getApiUrl()}/api/v2/trend-reports/priority-items`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setQueueState({
        status: "ok",
        reportDate: res.data?.report_date || null,
        items: res.data?.items || [],
      });
    } catch (e) {
      setQueueState((s) => ({ ...s, status: "error" }));
    }
  }, [currentUser, canManagePriority]);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  const [subscribersState, setSubscribersState] = useState({
    status: "loading",
    items: [],
  });
  const [dispatchesState, setDispatchesState] = useState({
    status: "loading",
    items: [],
  });
  const [audiencesState, setAudiencesState] = useState({
    status: "loading",
    items: [],
  });

  const fetchNewsletter = useCallback(async () => {
    if (!currentUser || !(canSendNewsletter || canManageSubscribers)) return;
    setSubscribersState((s) => ({ ...s, status: "loading" }));
    setDispatchesState((s) => ({ ...s, status: "loading" }));
    setAudiencesState((s) => ({ ...s, status: "loading" }));
    try {
      const token = await currentUser.getIdToken();
      const headers = { Authorization: `Bearer ${token}` };
      // Each section is fetched only for the role that shows it.
      const none = Promise.resolve({ data: {} });
      const [subs, sends, auds] = await Promise.all([
        canManageSubscribers
          ? axios.get(`${getApiUrl()}/api/v2/trend-reports/subscribers`, {
              headers,
            })
          : none,
        canSendNewsletter
          ? axios.get(`${getApiUrl()}/api/v2/trend-reports/dispatches?limit=20`, {
              headers,
            })
          : none,
        canManageSubscribers
          ? axios.get(`${getApiUrl()}/api/v2/trend-reports/audiences`, { headers })
          : none,
      ]);
      setSubscribersState({
        status: "ok",
        items: subs.data?.subscribers || [],
      });
      setDispatchesState({
        status: "ok",
        items: sends.data?.dispatches || [],
      });
      setAudiencesState({
        status: "ok",
        items: auds.data?.audiences || [],
      });
    } catch (e) {
      setSubscribersState((s) => ({ ...s, status: "error" }));
      setDispatchesState((s) => ({ ...s, status: "error" }));
      setAudiencesState((s) => ({ ...s, status: "error" }));
    }
  }, [currentUser, canSendNewsletter, canManageSubscribers]);

  useEffect(() => {
    fetchNewsletter();
  }, [fetchNewsletter]);

  const TabButton = ({ tabKey, config }) => {
    const isActive = activeTab === tabKey;
    return (
      <button
        onClick={() => setActiveTab(tabKey)}
        className={`relative flex flex-col md:flex-row items-center justify-center gap-1 sm:gap-2 md:gap-3 px-3 sm:px-4 md:px-6 py-2 md:py-3 rounded-xl transition-all duration-200 flex-1 min-w-0 ${
          isActive
            ? "bg-orange-500 text-white"
            : "text-gray-600 dark:text-gray-300 hover:bg-slate-200 dark:hover:bg-gray-700"
        }`}
      >
        <svg
          className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d={config.iconPath}
          />
        </svg>
        <span className="font-medium text-xs sm:text-sm truncate">
          {language === "zh" ? config.labelZh : config.labelEn}
        </span>
      </button>
    );
  };

  return (
    <div className="w-full h-screen overflow-y-auto flex bg-slate-100 dark:bg-dashboard-bg">
      <Sidebar />
      <LanguageSwitcher language={language} setLanguage={setLanguage} />
      <div className="flex-1 overflow-y-auto pt-16 pb-8">
        <div className="w-5/6 mx-auto px-4 lg:px-8">
          <div className="mt-10 lg:mt-0 flex flex-col items-center text-center px-4 mb-8">
            <h1 className="text-3xl lg:text-4xl font-bold text-gray-800 dark:text-white mb-1.5">
              Trend Reports
            </h1>
            <p className="text-sm lg:text-base text-gray-600 dark:text-slate-200 max-w-2xl">
              Browse every daily briefing we've generated
            </p>
          </div>

          {/* Tab navigation - only shown when there's more than one tab to pick from */}
          {Object.keys(TAB_CONFIG).length > 1 && (
            <div className="flex justify-center mb-8 w-full">
              <div className="inline-flex gap-1 sm:gap-2 p-1 sm:p-1.5 border border-gray-300/50 dark:border-gray-700 bg-gray-100 dark:bg-gray-800 shadow-xl rounded-xl w-full overflow-x-auto">
                {Object.entries(TAB_CONFIG).map(([key, config]) => (
                  <TabButton key={key} tabKey={key} config={config} />
                ))}
              </div>
            </div>
          )}

          {activeTab === "reports" && (
            <TrendList
              reports={reportsState.reports}
              status={reportsState.status}
              pagination={reportsState.pagination}
              latestDate={reportsState.latestDate}
              dateFrom={dateFrom}
              dateTo={dateTo}
              language={language}
              // A new range starts back on page 1.
              onDateChange={(range) => setListQuery({ ...range, page: null })}
              onPageChange={(p) => setListQuery({ page: p > 1 ? p : null })}
              onOpen={(reportDate) =>
                navigate(
                  {
                    pathname: `/trend-reports/${reportDate}`,
                    search: `?lang=${language}`,
                  },
                  // The detail page's back button returns to this page and range.
                  { state: { listSearch: location.search } },
                )
              }
            />
          )}
          {activeTab === "customization" && "customization" in TAB_CONFIG && (
            <CustomizationTab
              language={language}
              currentUser={currentUser}
              queue={queueState}
              onQueueChange={fetchQueue}
            />
          )}
          {activeTab === "newsletter" && "newsletter" in TAB_CONFIG && (
            <NewsletterTab
              language={language}
              currentUser={currentUser}
              subscribers={subscribersState}
              dispatches={dispatchesState}
              audiences={audiencesState}
              canSend={canSendNewsletter}
              canManageAudience={canManageSubscribers}
              onChange={fetchNewsletter}
            />
          )}
        </div>
      </div>
    </div>
  );
}
